import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getSelectedSearchItemId } from 'GeminiViewerComponent/features/asset/assetSlice';
import { getConfig } from 'GeminiViewerComponent/features/config/configSlice';
import { selectContentPanelItem } from 'GeminiViewerComponent/features/contentPanel/contentPanelItemSlice';
import { selectActiveTheme } from 'GeminiViewerComponent/features/globals/themeSlice';
import { trackImageViewed } from 'GeminiViewerComponent/helpers';
import { getCurrentNode } from '../Procedure/features/procedureSlice';
import {
    getActiveAssetActiveZone,
    getChildZones,
    getSelectedHotspotFromUser,
    selectActiveAsset,
    selectCompactZones,
} from '../../features/asset/assetSlice';
import {
    getIsEmptyHotspotVisible,
    getProcedureContentVersion,
    getShowOnlyByFilter,
    getTagFilters,
} from '../../features/globals/visibilitySlice';
import { MarzipanoManager } from './MarzipanoManager';
import { marzipanoStyles } from './styles';

const MarzipanoViewer = ({
    hotspotElements,
    onSelectHotspot,
    onShiftRightClick,
    onRightClick,
    onClick,
    isMultipleHotspotEnabled,
    isLoadedState,
    onClickRefreshItem,
    setActiveItem,
    handleMultipleHotspot,
    previewZone = null,
}) => {
    const dispatch = useDispatch();
    const theme = useSelector(selectActiveTheme);
    const panoStyles = marzipanoStyles(theme);

    const activeItemId = null;

    const config = useSelector(getConfig);
    const allZones = useSelector(selectCompactZones);
    const childZones = useSelector(getChildZones);
    const allZoneData = useMemo(() => {
        if (childZones && childZones?.length > 0) {
            return [...allZones, ...childZones];
        } else {
            return allZones;
        }
    }, [allZones, childZones]);
    const panelItem = useSelector(selectContentPanelItem);

    const showOnlyByFilter = useSelector(getShowOnlyByFilter);
    const selectedHotspotFromUser = useSelector(getSelectedHotspotFromUser);
    const activeAsset = useSelector(selectActiveAsset);
    let activeZone = useSelector(getActiveAssetActiveZone);
    const tagFilters = useSelector(getTagFilters);
    const searchItem = useSelector(getSelectedSearchItemId);
    const procedureContentVersion = useSelector(getProcedureContentVersion);
    const currentNode = useSelector(getCurrentNode);
    const isEmptyHotspotVisible = useSelector(getIsEmptyHotspotVisible);
    const zoneUpdateRequestRef = useRef(0);
    const sceneUpdateQueueRef = useRef(Promise.resolve());
    const lastTrackedImageEventRef = useRef('');
    const initialViewAppliedRef = useRef(null);

    const getZoneImageDetails = (zone) => {
        if (!zone) {
            return { imageType: 'none', imageCount: 0, imageSignature: '', imageSrcPath: '' };
        }

        if (Array.isArray(zone.cubeFaces) && zone.cubeFaces.length > 0) {
            const imageUrls = zone.cubeFaces
                .map((face) => face?.url)
                .filter(Boolean)
                .sort();

            return {
                imageType: 'cube_faces',
                imageCount: imageUrls.length,
                imageSignature: imageUrls.join('|'),
                imageSrcPath: imageUrls.join('|'),
            };
        }

        if (zone.equirect_image_url) {
            return {
                imageType: 'equirect',
                imageCount: 1,
                imageSignature: zone.equirect_image_url,
                imageSrcPath: zone.equirect_image_url,
            };
        }

        if (zone.flat_image_url) {
            return {
                imageType: 'flat',
                imageCount: 1,
                imageSignature: zone.flat_image_url,
                imageSrcPath: zone.flat_image_url,
            };
        }

        return { imageType: 'none', imageCount: 0, imageSignature: '', imageSrcPath: '' };
    };

    if (previewZone) {
        activeZone = previewZone;
    }

    useEffect(() => {
        initialViewAppliedRef.current = null;
    }, [activeAsset?.asset_id]);

    useEffect(() => {
        if (!config.user) return;
        window.viewerManager = new MarzipanoManager({
            config,
            initialZoneId: activeZone ? activeZone.zone_id : null,
            initialView: null,
            zoneItems: allZoneData,
            itemHotspots: activeZone?.items,
            tagFilters,
            showOnlyByFilter,
            isEmptyHotspotVisible,
            onShiftRightClick,
            onRightClick,
            onClick,
            isMultipleHotspotEnabled,
            handleMultipleHotspot,
        });

        window.viewerManager.init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        config.data,
        config.paths,
        config.user?.role,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        JSON.stringify(config.user?.audienceIds?.toSorted()),
        activeAsset?.asset_id,
    ]);

    // NOTE: This method previously used allZones and activeZone as dependencies.
    // This was causing some odd timing issues resulting in dragged hotspots jumpnig
    // back to their previous position even though they were successfully moved to
    // the new location. It was updated to use activeZone only.
    useEffect(() => {
        let isCancelled = false;
        const requestId = ++zoneUpdateRequestRef.current;

        async function updateScenes() {
            const viewerManager = window.viewerManager;
            const hasHotspots = (activeZone?.items?.length ?? 0) + (activeZone?.nav_links?.length ?? 0) > 0;
            const isOrbitZone = typeof activeZone?.orbit_frame === 'number' && activeZone.orbit_frame >= 0;

            // GeminiContainer creates the React roots for hotspots in a layout
            // effect. Do not create a scene without them on the initial render.
            // Zones with no hotspots can still render their image immediately.
            if (
                !activeZone ||
                isOrbitZone ||
                !viewerManager?.viewer ||
                (hasHotspots && !hotspotElements?.[activeZone.zone_id])
            ) {
                return;
            }

            // An equirectangular tiled image awaits its preview lookup. Keep
            // updates in order so two renders cannot destroy the same Marzipano
            // hotspot while one is still awaiting that lookup.
            if (isCancelled || window.viewerManager !== viewerManager) return;

            viewerManager.updateSceneData(isMultipleHotspotEnabled, activeZone, handleMultipleHotspot);

            await viewerManager.addScenesFromZones(
                [activeZone],
                hotspotElements,
                tagFilters,
                showOnlyByFilter,
                isEmptyHotspotVisible
            );

            if (
                isCancelled ||
                requestId !== zoneUpdateRequestRef.current ||
                window.viewerManager !== viewerManager
            ) {
                return;
            }

            const currentSceneData = viewerManager.getActiveSceneData?.();
            const shouldAnimateInPlace = String(currentSceneData?.zone_id) === String(activeZone.zone_id);

            viewerManager.switchSceneById(activeZone.zone_id);

            const hasNodeInitialView = currentNode?.initial_view != null;
            const highlightedItemId = searchItem || procedureContentVersion?.doAction?.item_id;
            const highlightedItem = activeZone?.items?.find((item) => item.item_id === highlightedItemId);
            const procedureInitialView = procedureContentVersion?.doAction?.initial_view;

            const defaultZoneView = {
                yaw: activeZone?.init_view_yaw,
                pitch: activeZone?.init_view_pitch,
                fov: activeZone?.init_view_fov,
            };

            const nodeInitialView = hasNodeInitialView
                ? {
                    yaw: procedureInitialView?.yaw,
                    pitch: procedureInitialView?.pitch,
                    fov: procedureInitialView?.zoom,
                }
                : null;

            const highlightedItemView = highlightedItem
                ? {
                    yaw: highlightedItem?.yaw,
                    pitch: highlightedItem?.pitch,
                    fov: hasNodeInitialView ? procedureInitialView?.zoom : defaultZoneView.fov,
                    x: highlightedItem?.flat_x,
                    y: highlightedItem?.flat_y,
                }
                : null;

            const targetViewParams = {
                ...(highlightedItemView || nodeInitialView || defaultZoneView),
            };

            const restoreLock = window.__geminiSkipSceneViewUpdate;
            const isRestoreLocked =
                restoreLock?.zoneId === activeZone?.zone_id && typeof restoreLock?.until === 'number'
                    ? restoreLock.until > Date.now()
                    : false;

            // Include the target item so moving to another item in the same zone re-triggers the view change
            const zoneKey = activeZone?.zone_id
                ? `${activeZone.zone_id}:${highlightedItemId ?? ''}:${currentNode?.node_id ?? currentNode?.id ?? ''}`
                : '';
            const hasAppliedInitialView = zoneKey ? initialViewAppliedRef.current === zoneKey : false;
            const isInitialViewPending = !isRestoreLocked && zoneKey && !hasAppliedInitialView;

            if (isInitialViewPending) {
                viewerManager.setViewParams(
                    { ...targetViewParams },
                    { animate: shouldAnimateInPlace }
                );
                initialViewAppliedRef.current = zoneKey;
            }

            const { imageType, imageCount, imageSignature, imageSrcPath } = getZoneImageDetails(activeZone);
            const eventKey = `${activeZone.zone_id}:${imageType}:${imageSignature}`;

            if (imageCount > 0 && lastTrackedImageEventRef.current !== eventKey) {
                trackImageViewed({
                    zoneId: activeZone.zone_id,
                    zoneName: activeZone.display_name,
                    imageType,
                    imageCount,
                    imageSrcPath,
                    assetId: activeAsset?.asset_id,
                    assetTypeId: activeAsset?.asset_type_id,
                    displayName: activeAsset?.display_name,
                    clientId: config?.data?.client?.client_id,
                    clientName: config?.data?.client?.display_name,
                });
                lastTrackedImageEventRef.current = eventKey;
            }

            const pendingViewRestore = window.__geminiPendingViewRestore;
            if (pendingViewRestore?.zoneId === activeZone.zone_id) {
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        try {
                            window.geminiViewManager?.setViewParams?.(pendingViewRestore.params, {
                                animate: shouldAnimateInPlace,
                            });
                            window.geminiViewManager?.updateView?.();
                        } finally {
                            delete window.__geminiPendingViewRestore;
                        }
                    });
                });
            }

            if (activeItemId) {
                // Show current node id
                if (activeZone?.items?.find((item) => item.item_id === activeItemId)) {
                    window.showHotspot(activeItemId);
                } else {
                    if (setActiveItem) {
                        dispatch(setActiveItem({}));
                    }
                }
            }
            let panoEl = document.getElementById('pano');
            if (panoEl) {
                if (isMultipleHotspotEnabled) {
                    panoEl.style.cursor = 'crosshair';
                } else {
                    panoEl.style.cursor = 'default';
                }
            }
        }
        sceneUpdateQueueRef.current = sceneUpdateQueueRef.current
            .catch(() => undefined)
            .then(updateScenes)
            .catch((error) => {
                console.error('Unable to update Marzipano scene', error);
            });
        return () => {
            isCancelled = true;
        };
        // NOTE: This will be called twice when active zone changes - because after the first
        // render for zone change the hotspotElements are created which causes another render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        searchItem,
        activeZone,
        hotspotElements,
        tagFilters,
        isMultipleHotspotEnabled,
        isEmptyHotspotVisible,
        showOnlyByFilter,
        procedureContentVersion?.doAction?.item_id,
        procedureContentVersion?.doAction?.initial_view?.yaw,
        procedureContentVersion?.doAction?.initial_view?.pitch,
        procedureContentVersion?.doAction?.initial_view?.zoom,
        currentNode,
    ]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (window.viewerManager !== undefined && window.showHotspot !== undefined) {
            window.showHotspot(activeItemId);
        }
    }, [activeItemId]);

    useEffect(() => {
        if (
            activeItemId !== undefined &&
            onSelectHotspot !== undefined &&
            onSelectHotspot.canExecute &&
            panelItem !== null &&
            selectedHotspotFromUser === true
        ) {
            onSelectHotspot.execute(panelItem.ZoneId, panelItem.ItemId);
        }

        // eslint-disable-next-line
    }, [panelItem, selectedHotspotFromUser]);

    let hasImage =
        activeZone &&
        (activeZone.cubeFaces !== undefined ||
            activeZone.equirect_image_url !== undefined ||
            activeZone.flat_image_url !== undefined);

    let message = '';
    if (!hasImage && isLoadedState) {
        message = 'This Zone Requires An Image';
    }

    return (
        <>
            <div
                id="pano"
                tabIndex="0"
                className={panoStyles.panoWrapper}
                onKeyDown={(e) => {
                    if (e.code === 'KeyR') {
                        onClickRefreshItem();
                    }
                }}
            ></div>

            {!hasImage && (
                <div
                    style={{
                        position: 'relative',
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0, 0, 0, 1.0)',
                    }}
                >
                    <div
                        style={{
                            fontSize: '24px',
                            color: 'white',
                            position: 'relative',
                            top: '50%',
                            textAlign: 'center',
                        }}
                    >
                        {message}
                    </div>
                </div>
            )}

            {config.data.settings.viewControlButtons && (
                <>
                    <button id="viewUp" className="viewControlButton viewControlButton-1">
                        <img className="icon" alt="Pan Up" src={config.paths.BASE_STORE_URL + 'img/up.png'}></img>
                    </button>
                    <button id="viewDown" className="viewControlButton viewControlButton-2">
                        <img className="icon" alt="Pan Down" src={config.paths.BASE_STORE_URL + 'img/down.png'}></img>
                    </button>
                    <button id="viewLeft" className="viewControlButton viewControlButton-3">
                        <img className="icon" alt="Pan Left" src={config.paths.BASE_STORE_URL + 'img/left.png'}></img>
                    </button>
                    <button id="viewRight" className="viewControlButton viewControlButton-4">
                        <img className="icon" alt="Pan Right" src={config.paths.BASE_STORE_URL + 'img/right.png'}></img>
                    </button>
                    <button id="viewIn" className="viewControlButton viewControlButton-5">
                        <img className="icon" alt="Zoom In" src={config.paths.BASE_STORE_URL + 'img/plus.png'}></img>
                    </button>
                    <button id="viewOut" className="viewControlButton viewControlButton-6">
                        <img className="icon" alt="Zoom Out" src={config.paths.BASE_STORE_URL + 'img/minus.png'}></img>
                    </button>
                </>
            )}
        </>
    );
};

export { MarzipanoViewer };
